import React, { useState } from 'react';
import { ProjectSubscription } from '../types/subscription';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cancelSubscription } from '../api/subscription-api';
import { handleApiError } from '@/shared/api/requests';
import { useUser } from '@/shared/hooks/useUser';
import { Calendar, User, Lock, Unlock } from 'lucide-react';

interface SubscriptionCardProps {
  subscription: ProjectSubscription;
  onCancelled: () => void;
}

export const SubscriptionCard: React.FC<SubscriptionCardProps> = ({ 
  subscription, 
  onCancelled 
}) => {
  const { user } = useUser();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const canModify = user?.email === subscription.created_by || user?.is_superuser;

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await cancelSubscription(subscription.id);
      setShowCancelDialog(false);
      onCancelled();
    } catch (err) {
      const errorMessage = handleApiError(err);
      alert(`Failed to cancel subscription: ${errorMessage}`);
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <>
      <Card className="hover:shadow-md transition-shadow">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-xl">{subscription.project_name}</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">{subscription.plan_name || 'No Plan'}</p>
            </div>
            <div className="flex items-center gap-2">
              {subscription.is_active && subscription.has_access ? (
                <Badge variant="default" className="bg-accent text-accent-foreground border border-border">
                  <Unlock className="w-3 h-3 mr-1" />
                  Active
                </Badge>
              ) : (
                <Badge variant="secondary" className="bg-destructive/10 text-destructive border border-destructive/20">
                  <Lock className="w-3 h-3 mr-1" />
                  Inactive
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* Subscription Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="w-4 h-4" />
                <span>
                  Expires: {subscription.has_access ? formatDate(subscription.expires_at) : 'Expired'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <User className="w-4 h-4" />
                <span>Created by: {subscription.created_by || 'Unknown'}</span>
              </div>
            </div>

            {/* Actions */}
            {canModify && subscription.is_active && (
              <div className="flex gap-2 pt-3 border-t border-border">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowCancelDialog(true)}
                  className="w-full"
                >
                  Cancel Subscription
                </Button>
              </div>
            )}

            {!canModify && subscription.is_active && (
              <div className="text-xs text-muted-foreground italic pt-3 border-t border-border">
                Only {subscription.created_by} can modify this subscription.
              </div>
            )}

            {!subscription.is_active && (
              <div className="text-sm text-muted-foreground pt-3 border-t border-border">
                This subscription has been cancelled.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your subscription to <strong>{subscription.project_name}</strong>?
              <br /><br />
              You will lose access to this project's completed data at the end of your current billing period
              ({formatDate(subscription.expires_at)}).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
              disabled={cancelling}
            >
              Keep Subscription
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={cancelling}
            >
              {cancelling ? 'Cancelling...' : 'Yes, Cancel Subscription'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

